import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import Marquee from "@/components/site/Marquee";
import { HOME } from "@/constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] },
  },
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/books?featured=true").then((r) => setFeatured(r.data));
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3)));
  }, []);

  return (
    <main className="pt-16 md:pt-20">
      {/* BRAND MARK — hero */}
      <BrandMark />

      {/* MARQUEE */}
      <Marquee
        items={[
          "The Closing Blood Tear",
          "Available Now",
          "The Hand of Hirohito",
          "The Price of Moonlight",
          "Coming Soon",
          "Aquii Lee",
        ]}
      />

      {/* FEATURED CATALOG */}
      <section className="px-6 md:px-10 py-32 md:py-40">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="flex items-end justify-between mb-16 md:mb-24"
          >
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-bone-dim)] mb-6">
                — Featured · Winter 2025
              </div>
              <h2 className="font-serif-vv text-5xl md:text-7xl leading-[0.95] tracking-tight">
                Three books
                <br />
                <span className="italic text-[color:var(--vv-bone-dim)]">
                  we cannot stop
                </span>{" "}
                thinking about.
              </h2>
            </div>
            <Link
              to="/catalog"
              className="hidden md:inline-block text-xs uppercase tracking-[0.3em] vv-link text-[color:var(--vv-ember)]"
            >
              All Titles →
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-20">
            {featured.map((b, i) => (
              <motion.article
                key={b.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <Link to={`/book/${b.slug}`} className="block">
                  <div className="book-frame relative aspect-[3/4] overflow-hidden border vv-line p-8 md:p-12 bg-[color:var(--vv-surface)]">
                    <motion.img
                      src={b.cover_url}
                      alt={b.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
                    />
                    {b.coming_soon && (
                      <div className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase text-[color:var(--vv-ember)] border border-[color:var(--vv-ember)] px-2 py-1 bg-[color:var(--vv-bg)]/60 backdrop-blur">
                        Coming Soon
                      </div>
                    )}
                  </div>
                  <div className="mt-6 flex justify-between items-baseline">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)]">
                        {b.genre} · {b.year}
                      </div>
                      <h3 className="mt-2 font-serif-vv text-3xl leading-tight">
                        {b.title}
                      </h3>
                      <div className="mt-1 font-serif-vv italic text-lg text-[color:var(--vv-bone-dim)]">
                        {b.author_name}
                      </div>
                    </div>
                    <span className="text-xs text-[color:var(--vv-ember)] opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="px-6 md:px-10 py-32 md:py-40 border-t vv-line">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-bone-dim)] mb-16 md:mb-24">
            — A Manifesto in Three Movements
          </div>
          {[
            {
              n: "01",
              title: "Dark worlds. Toward the light.",
              body: "Every story we publish starts somewhere it should not — a locked room, a buried letter, a secret worn thin. We stay with the dark until it opens.",
            },
            {
              n: "02",
              title: "Without love there's no life.",
              body: "The people our characters love are the reason they keep moving. Not because love is easy, but because it is the only currency that still pays out when everything else has been spent.",
            },
            {
              n: "03",
              title: "Truth refuses to stay buried.",
              body: "Every A. Lee Creations title is designed around a single, insistent question: what does the story know that its narrator does not? The answer usually arrives on the last page.",
            },
            {
              n: "04",
              title: "There are many roads to a destination.",
              body: "No two characters walk the same way home. We publish fiction that honors the long, crooked, personal route — the one that costs something, and delivers you somewhere you did not know you were going.",
            },
          ].map((c, i) => (
            <motion.div
              key={c.n}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.15 }}
              className="grid grid-cols-12 gap-6 md:gap-12 py-14 border-t vv-line first:border-t"
            >
              <div className="col-span-12 md:col-span-2 font-serif-vv text-6xl md:text-8xl leading-none text-[color:var(--vv-bone-dim)]/40">
                {c.n}
              </div>
              <div className="col-span-12 md:col-span-6">
                <h3 className="font-serif-vv text-4xl md:text-5xl leading-tight">
                  {c.title}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-4 text-[color:var(--vv-bone-dim)] leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: c.body }} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* JOURNAL PREVIEW */}      <section className="px-6 md:px-10 py-32 md:py-40 border-t vv-line">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex items-end justify-between mb-16"
          >
            <h2 className="font-serif-vv text-5xl md:text-7xl leading-none tracking-tight">
              From the <em>Journal</em>
            </h2>
            <Link
              to="/journal"
              className="text-xs uppercase tracking-[0.3em] vv-link text-[color:var(--vv-ember)]"
            >
              All Dispatches →
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.1 }}
                className="group border-t vv-line pt-6"
              >
                <Link to={`/journal/${p.slug}`}>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-ember)]">
                    {p.kicker}
                  </div>
                  <h3 className="mt-4 font-serif-vv text-3xl leading-tight group-hover:italic transition-all duration-500">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[color:var(--vv-bone-dim)]">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 text-xs uppercase tracking-[0.3em] text-[color:var(--vv-ember)]">
                    Read →
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function BrandMark() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, -80]);
  const scale = useTransform(scrollY, [0, 1000], [1, 1.04]);

  return (
    <section className="relative overflow-hidden pt-20 md:pt-28">
      <motion.div style={{ y, scale }} className="relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-32 md:py-48 grid grid-cols-12 gap-8 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 md:col-span-5 relative"
          >
            <div className="relative aspect-square w-full max-w-[480px] mx-auto">
              <div className="absolute -inset-4 border vv-line" />
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_story-stage-18/artifacts/ygm2ivyo_ac177a3c-e752-4d60-986d-4a6b6608ec1e.png"
                alt="A. Lee Creations"
                className="relative w-full h-full object-cover"
              />
              <div className="absolute -bottom-6 left-0 right-0 flex justify-between text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)] px-2">
                <span>The Mark</span>
                <span>Est. — Independent</span>
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)] mb-8"
            >
              — The House
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif-vv text-5xl md:text-8xl leading-[0.9] tracking-tight"
            >
              Where words
              <br />
              <em className="text-[color:var(--vv-ember)]">forge worlds.</em>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
              className="mt-10 font-serif-vv italic text-2xl md:text-3xl leading-snug text-[color:var(--vv-ember)]"
            >
              You've made it. Pull up a chair.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: 0.3, ease: [0.19, 1, 0.22, 1] }}
              className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-[color:var(--vv-bone)]/85 max-w-xl"
            >
              <p>
                This is the home of <span className="text-[color:var(--vv-bone)] font-medium">Aquii Lee</span>,
                creator of stories packed with loyalty, betrayal, redemption,
                power, and the kind of decisions that keep people awake at 2
                a.m. wondering how everything went sideways.
              </p>
              <p>
                Whether you're here for{" "}
                <em className="text-[color:var(--vv-bone)]">The Closing Blood Tear</em>, the{" "}
                <em className="text-[color:var(--vv-bone)]">Blood Redemption</em> series,
                or just trying to figure out what kind of person spends years
                creating worlds where nobody gets a free pass — you're in the
                right place.
              </p>
              <p>
                Around here, heroes have flaws, villains have reasons, and
                everybody's carrying something heavier than they let on.
              </p>
              <p>
                Take a look around. Read a chapter. Meet the characters. And
                remember — <em>everybody loves the truth until it shows up at
                their front door.</em>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
              className="mt-12 flex flex-wrap items-center gap-6"
            >
              <Link
                to="/catalog"
                data-testid={HOME.ctaCatalog}
                className="group inline-flex items-center gap-3 border vv-line px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors duration-500"
              >
                Enter Catalog
                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </Link>
              <Link
                to="/journal"
                data-testid={HOME.ctaJournal}
                className="text-xs uppercase tracking-[0.3em] text-[color:var(--vv-bone-dim)] vv-link"
              >
                Read the Journal
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
